import useSWR from 'swr';
import { fetchEndpointData } from '@/utils/fetch.client';

export interface BetaInvitation {
  id: string;
  email: string;
  name: string | null;
  invited_by_user_id: string;
  created_at: string;
  expires_at: string | null;
  accepted_at: string | null;
  accepted_user_id: string | null;
  welcome_email_sent_at: string | null;
  status: 'pending' | 'accepted';
}

interface ListInvitationsResponse {
  invitations: BetaInvitation[];
  total: number;
}

export type InvitationStatusFilter = 'all' | 'pending' | 'accepted';

export interface AdminInvitationsQuery {
  status: InvitationStatusFilter;
  page: number;
  pageSize: number;
}

function buildKey({ status, page, pageSize }: AdminInvitationsQuery) {
  const params = new URLSearchParams();
  if (status !== 'all') params.set('status', status);
  params.set('limit', String(pageSize));
  params.set('offset', String((page - 1) * pageSize));
  return `/api/v1/admin/beta-invitations?${params.toString()}`;
}

export function useAdminBetaInvitations(query: AdminInvitationsQuery) {
  const key = buildKey(query);
  const { data, mutate, isLoading, error } = useSWR(key, () => fetchEndpointData<ListInvitationsResponse>(key), {
    keepPreviousData: true,
  });
  return {
    invitations: data?.invitations ?? [],
    total: data?.total ?? 0,
    mutate,
    isLoading,
    error,
  };
}

export async function createBetaInvitation(email: string, name?: string) {
  return fetchEndpointData<BetaInvitation>('/api/v1/admin/beta-invitations', {
    method: 'POST',
    body: { email, name: name?.trim() || undefined },
  });
}

export async function revokeBetaInvitation(id: string) {
  return fetchEndpointData<{ ok: boolean }>(`/api/v1/admin/beta-invitations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function resendBetaInvitationEmail(id: string) {
  return fetchEndpointData<BetaInvitation>(`/api/v1/admin/beta-invitations/${encodeURIComponent(id)}/resend`, {
    method: 'POST',
  });
}
